import { makeStyles } from '@material-ui/styles'
import ResourceUsageIndicator from 'app/plugins/infrastructure/components/common/ResourceUsageIndicator'
import TitleWithInfoIcon from 'core/components/TitleWithInfoIcon'
import PicklistField from 'core/components/validatedForm/DropdownField'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import KeyValuesField from 'core/components/validatedForm/KeyValuesField'
import ListTableField from 'core/components/validatedForm/ListTableField'
import TextField from 'core/components/validatedForm/TextField'
import Badge from 'core/elements/badge'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useSelectorWithParams from 'core/hooks/useSelectorWithParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { Route } from 'core/plugins/route'
import Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import {
  addHostToAggregate,
  createHostAggregate,
  getResources,
  listHypervisors,
  listResmgrHosts,
  listResourceInventory,
  updateHostAggregateMetadata,
  updateResourceInventory,
} from '../actions'
import ClusterHostGroupsPicklist from '../clusters/ClusterHostGroupsPicklist'
import { mergeHypervisorsWithHosts, toGiB, toMHz } from '../helpers'
import ExpandableHostAggregateMetadataView from '../hosts/ExpandableHostAggregateMetadataView'
import { ResourceUsageIndicators, RespondingCell, RoleStatusesCell } from '../hosts/HostsListPage'
import { hypervisorsSelector, resmgrHostsSelector } from '../selectors'

const useStyles = makeStyles<Theme>((theme) => ({
  withUnits: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  unitsDropdown: {
    position: 'relative',
    bottom: 1,
  },
  fields: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    alignItems: 'flex-end',
    gap: theme.spacing(2),
    height: 'fit-content',
  },
}))

interface Props {
  addRoute: Route
}

export const hypervisorColumns = [
  {
    id: 'hypervisor_hostname',
    label: 'Host',
  },
  {
    id: 'id',
    label: 'ID',
    display: false,
  },
  {
    id: 'host_ip',
    label: 'IP Address',
  },

  {
    id: 'hostAggregateMetadata',
    label: 'Host Aggregates',
    render: (value, item) => (
      <ExpandableHostAggregateMetadataView itemName={item?.name} value={value} />
    ),
    display: false,
  },
  {
    id: 'responding',
    label: (
      <TitleWithInfoIcon
        titleText="Connection Status"
        tooltipMsg={<div>Indicates if the host agent can connect to the management plane</div>}
      />
    ),
    // Don't want 'Responding' string to line wrap, use Text component
    render: (value, item) => <RespondingCell value={value} item={item} />,
    display: false,
  },
  {
    id: 'roleStatuses',
    label: (
      <TitleWithInfoIcon
        titleText="Role Status"
        tooltipMsg={<div>Shows the application status of all roles assigned to this host</div>}
      />
    ),
    render: (value, item) => <RoleStatusesCell value={value} item={item} />,
    display: false,
  },
  {
    id: 'schedulingStatus',
    label: 'Scheduling',
    render: (value, item) => {
      if (!item.service) {
        return 'N/A'
      }
      return (
        <div>
          {value}
          {item?.service?.disabled_reason && (
            <div style={{ marginTop: '8px', display: 'flex', gap: '8px', alignItems: 'center' }}>
              <Badge
                variant="warning"
                text={item?.running_vms > 0 ? 'Entering Maintenance Mode' : 'Maintenance Mode'}
                ellipsisAt={25}
                tooltipBody={
                  item?.running_vms > 0
                    ? 'Migrations may be in-progress, or may need manual intervention to migrate remaining active VMs. Please check maintenance mode progress within host details.'
                    : null
                }
              />
            </div>
          )}
        </div>
      )
    },
    display: false,
  },
  {
    id: 'clusterName',
    label: 'Cluster',
    display: false,
  },
  {
    id: 'physicalNetworkLabels',
    label: 'Physical Network Label',
    width: 100,
    display: false,
    render: (value) => {
      const physicalNetworkLabels = value || []
      return <Text variant="body2"> {physicalNetworkLabels?.join(', ')}</Text>
    },
  },
  {
    id: 'compute',
    label: 'Compute',
    render: (value, item) => {
      return (
        <ResourceUsageIndicators
          usageValueConverter={{ fn: toMHz, units: 'MHz' }}
          usageStats={item?.usage?.compute}
          allocationStats={item?.usage?.computeAllocation}
        />
      )
    },
    display: false,
  },
  {
    id: 'memory',
    label: 'Memory',
    render: (value, item) => (
      <ResourceUsageIndicators
        allocationValueConverter={{ fn: toGiB, units: 'GiB' }}
        usageStats={item?.usage?.memory}
        allocationStats={item?.usage?.memoryAllocation}
      />
    ),
    display: false,
  },
  {
    id: 'storage',
    label: 'Storage',
    render: (value, item) => {
      return (
        <>
          <ResourceUsageIndicator
            units={item?.usage?.disk?.units}
            label="Root Disk Used"
            stats={item?.usage?.disk}
          />
          <ResourceUsageIndicator
            units={item?.usage?.ephemeralDisk?.units}
            label="Ephemeral Disk Used"
            stats={item?.usage?.ephemeralDisk}
          />
        </>
      )
    },
    display: false,
  },
]

export default function AddHostAggregateModal({ addRoute }: Props) {
  const { history } = useReactRouter()
  const classes = useStyles()
  const defaultParams = {
    name: '',
    availabilityZone: '',
    metadata: [],
    hosts: [],
  }
  const { params, getParamsUpdater, setParams } = useParams(defaultParams)
  const [submitting, setSubmitting] = useState(false)
  const [resources, setResources] = useState([])

  const { update, updating, error, reset } = useUpdateAction(createHostAggregate)
  const {
    update: addHost,
    updating: addingHost,
    error: errorAddingHost,
    reset: resetAddHost,
  } = useUpdateAction(addHostToAggregate)
  const {
    update: updateMetadata,
    updating: updatingMetadata,
    error: errorUpdatingMetadata,
    reset: resetUpdateMetadata,
  } = useUpdateAction(updateHostAggregateMetadata)

  const { loading: loadingHosts } = useListAction(listResmgrHosts)
  const hosts = useSelector(resmgrHostsSelector)
  const { loading: loadingHypervisors } = useListAction(listHypervisors, {
    params: {},
  })
  const hypervisorsList = useSelectorWithParams(hypervisorsSelector, {} as any)

  // Filter hosts by selected cluster
  const filteredHostByCluster = useMemo(() => {
    return hosts.filter((host) => host.clusterName === params.availabilityZone)
  }, [hosts, params.availabilityZone])

  const validHypervisors = useMemo(() => {
    return mergeHypervisorsWithHosts(hypervisorsList, filteredHostByCluster)
  }, [filteredHostByCluster, hypervisorsList])

  useEffect(() => {
    const getAllRsources = async () => {
      const resources = await getResources()
      setResources(resources)
    }

    getAllRsources()
  }, [])

  const submitForm = useCallback(async () => {
    setSubmitting(true)
    const body = {
      aggregate: {
        name: params.name,
        availability_zone: params.availabilityZone,
      },
    }
    const metadata = params.metadata.reduce((accum, pair) => {
      // Don't let user set the availability zone
      if (pair.key === 'availability_zone') {
        return accum
      }
      return {
        ...accum,
        [pair.key]: pair.value,
      }
    }, {})

    const metadataBody = {
      set_metadata: {
        metadata,
      },
    }

    const { success, response } = await update({ body })
    const aggregateId = response?.id

    if (success) {
      updateMetadata({ id: aggregateId, body: metadataBody })
      const promises = params.hosts.map((host) => {
        return addHost({
          id: aggregateId,
          body: {
            add_host: {
              host: host?.service?.host,
            },
          },
        })
      })

      const filterResourcesUUIDByHostName = resources
        .filter((resource) => {
          const filteredHostByName = params.hosts.map((host) => host?.hypervisor_hostname)
          return filteredHostByName.includes(resource.name)
        })
        .map((resource) => resource.uuid)

      const allocationRatioPromises = filterResourcesUUIDByHostName.map(async (uuid) => {
        const { resource_provider_generation, inventories } = await listResourceInventory({
          id: uuid,
        })

        const filteredInvetories = Object.keys(inventories).reduce((accum, current) => {
          return {
            ...accum,
            [current]: {
              ...inventories[current],
              allocation_ratio: !!params[current]
                ? Number(params[current])
                : inventories[current].allocation_ratio,
            },
          }
        }, {})

        return updateResourceInventory({
          id: uuid,
          body: {
            inventories: filteredInvetories,
            resource_provider_generation: resource_provider_generation,
          },
        })
      })

      const data = await Promise.allSettled([...promises, ...allocationRatioPromises])
      if (data) handleClose()
    }
    setSubmitting(false)
  }, [params])

  const handleClose = () => {
    setParams(defaultParams)
    reset()
    resetAddHost()
    resetUpdateMetadata()
    history.push(routeManager.routes.openstack.hostAggregates.path())
  }

  return (
    <ModalForm
      route={addRoute}
      title={`Create Host Aggregate`}
      onSubmit={submitForm}
      onClose={handleClose}
      submitting={submitting}
      error={error}
      submitTitle={`Create Host Aggregate`}
    >
      <>
        <FormFieldSection title="Host Aggregate Configuration" alignContentLeft>
          <div className={classes.fields}>
            <TextField
              id="name"
              label="Name"
              onChange={getParamsUpdater('name')}
              value={params.name}
              required
            />
            <PicklistField
              id="availabilityZone"
              label="Cluster"
              info="Choose the cluster that this host aggregate should be placed on"
              onChange={getParamsUpdater('availabilityZone')}
              value={params.availabilityZone}
              disableClusterWithoutHosts
              fullWidth
              required
              DropdownComponent={ClusterHostGroupsPicklist}
            />
          </div>
          <KeyValuesField
            id="metadata"
            label="Metadata"
            value={params.metadata}
            onChange={getParamsUpdater('metadata')}
            addLabel="Add Metadata"
            allowMultipleValues
          />
        </FormFieldSection>
        <FormFieldSection title="Select Hosts to Add" alignContentLeft>
          <ListTableField
            id="hosts"
            data={validHypervisors}
            loading={loadingHypervisors || loadingHosts}
            columns={hypervisorColumns}
            onChange={getParamsUpdater('hosts')}
            value={params.hosts}
            uniqueIdentifier="id"
            searchTargets={['hypervisor_hostname']}
            emptyText={
              params.availabilityZone
                ? 'No matching hosts found for selected cluster'
                : 'Please select a cluster first'
            }
            multiSelection
          />
        </FormFieldSection>
      </>
    </ModalForm>
  )
}
